import { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { Box, CircularProgress } from '@mui/material';
import PdfjsWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = PdfjsWorkerUrl;

interface Field {
  id: number;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  filled: boolean;
  onClick: () => void;
  renderContent?: React.ReactNode;
}

interface Props {
  url: string;
  fields: Field[];
}

interface RenderedPage {
  pageNum: number;
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
}

export default function PdfViewer({ url, fields }: Props) {
  const [pages, setPages] = useState<RenderedPage[]>([]);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    setLoading(true);

    const render = async () => {
      try {
        const pdf = await pdfjsLib.getDocument({ url, withCredentials: false }).promise;
        const rendered: RenderedPage[] = [];

        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const scale = 1.5;
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await page.render({
            canvasContext: canvas.getContext('2d')!,
            viewport,
            canvas,
          }).promise;

          if (!mountedRef.current) return;

          rendered.push({
            pageNum: i,
            canvas,
            width: viewport.width,
            height: viewport.height,
          });
        }

        if (mountedRef.current) {
          setPages(rendered);
          setLoading(false);
        }
      } catch (e) {
        console.error('PDF render error', e);
        if (mountedRef.current) setLoading(false);
      }
    };

    render();
    return () => { mountedRef.current = false; };
  }, [url]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ overflowY: 'auto', height: '100%', bgcolor: '#525659', py: 2 }}>
      {pages.map(({ pageNum, canvas, width, height }) => {
        const pageFields = fields.filter(f => f.page === pageNum);

        return (
          <Box
            key={pageNum}
            sx={{
              position: 'relative',
              mx: 'auto',
              mb: 2,
              boxShadow: 4,
              display: 'block',
              width: `${width}px`,
              height: `${height}px`,
              maxWidth: '100%',
            }}
          >
            {/* PDF page canvas */}
            <canvas
              ref={el => {
                if (el && el !== canvas) {
                  // copy rendered canvas into the DOM canvas
                  el.width = canvas.width;
                  el.height = canvas.height;
                  el.getContext('2d')?.drawImage(canvas, 0, 0);
                }
              }}
              width={width}
              height={height}
              style={{ display: 'block', width: '100%', height: '100%' }}
            />

            {/* Field overlays — positioned relative to this page */}
            {pageFields.map(field => (
              <Box
                key={field.id}
                onClick={field.onClick}
                sx={{
                  position: 'absolute',
                  left: `${field.x}%`,
                  top: `${field.y}%`,
                  width: `${field.width}%`,
                  height: `${field.height}%`,
                  border: '2px solid',
                  borderColor: field.filled ? 'success.main' : 'primary.main',
                  bgcolor: field.filled ? 'success.main' + '22' : 'primary.main' + '22',
                  borderRadius: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s, background-color 0.15s',
                  '&:hover': {
                    borderColor: field.filled ? 'success.dark' : 'primary.dark',
                    bgcolor: field.filled ? 'success.main' + '44' : 'primary.main' + '44',
                  },
                }}
              >
                {field.renderContent}
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}
